import { PlugZap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useIntegrationsStatusSummary } from "@/hooks/useIntegrationsStatusSummary";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";
import type { AppPlatform } from "@/types";
import Button from "@/components/ui/Button";
import { consoleName } from "@/lib/needsYouCopy";

/**
 * Connect store / grant access card — upper-warning matrix row A
 * (experience-and-ui.md § 3a "Upper warnings — two surfaces, one look",
 * PO direction Doc A 2026-07-26; ENG-PO-DOCS-DIRECTIVES-0903 item A7).
 *
 * Source: docs/product/experience-and-ui.md § 3a item 9 (canonical card
 *         description) + § 3a "Upper warnings" matrix (placement +
 *         connected/not-connected split — supersedes the prior
 *         Home-only placement) + § 9g (the Tier-1 console connect is
 *         preferred, NOT a gate — only experiment EXECUTION gates on a
 *         later connect) + § Progressive integration tiers (the connect
 *         nudge MUST follow the operator into the app rather than being
 *         a one-time onboarding ask).
 *
 * **Single shared mount (A7).** Rendered ONCE from the app shell
 * (`components/layout/Layout.tsx`), above every routed page's
 * `<Outlet />` — NOT a per-page copy. `isHome` is resolved by the shell
 * from `useLocation()` so this component stays a pure function of its
 * props.
 *
 * **PO matrix (verbatim table, § 3a):**
 *   | Rule                                  | Home dashboard        | All other pages |
 *   |----------------------------------------|------------------------|------------------|
 *   | Connect your store console (not connected) | Showing as it is now | Showing as it is now on home dashboard |
 *   | Connect your store console (connected)     | Showing health status | Not showing |
 *
 * i.e. the not-connected nudge renders identically on every page; the
 * connected "health status" confirmation is Home-only (ENG-STATUS-COLOUR
 * -MISUSE: this is a real connection STATE, so the success ramp is a
 * legitimate status use, not a category/tier misuse).
 *
 * **Semantic ramp (§ 3a, PO verbatim: "Connect your store console" = the
 * CRITICAL error ramp).** Large surfaces stay on the pre-2026-08-26
 * Tailwind `danger-*` ramp per UI_COMPONENT_CONTRACT.md's "Destructive
 * -action + connector surfaces" row — the BRANDING semantic ramp
 * (`--color-error` etc.) is scoped to Badges, not large card surfaces.
 *
 * Session detection is APP-scoped + platform-gated: an iOS app reads the
 * Apple App Store Connect session (`useAscSessionStatus`), an Android app
 * reads the Google Play Console session (`usePlayConsoleSessionStatus`).
 * BOTH hooks take an APP id — their routes are
 * `/api/v1/integrations/{asc,play-console}-session/{app_id}/status` and both
 * `_load_app_or_404` the path id against `App.app_id` before reading any
 * credentials. Handing either one a WORKSPACE id 404s (`app_not_found`), the
 * query settles to error with `data === undefined`, and this card then renders
 * its CRITICAL not-connected nudge — on every page, forever, against a fully
 * live console session. The Play side shipped with exactly that defect
 * (`53362bfbf`, carried through A7) and it is why every Android workspace saw
 * a permanent "Connect your store console" warning; pinned now by
 * `verify_dashboard_runtime.py::verify_upper_warnings_shared_mount`.
 * Both status hooks expose a `connected: boolean`; the card hides when the
 * platform-matched session is connected and the shell is not on Home.
 *
 * The Tier-2 "Add richer signals" enrichment card is a SEPARATE sibling
 * component (`AddRicherSignalsCard.tsx`) as of A7 — the two used to be
 * nested in one card, which the PO direction explicitly forbids
 * ("ADD RICHER SIGNALS and Connect your store console are two different
 * things. They must be separated").
 */

interface ConnectStoreCardProps {
  /**
   * The active app's platform. When `null` (platform not yet resolved) the
   * card renders nothing — the session-status read can't be platform-gated
   * without it, and a Tier-1 nudge with no platform target is meaningless.
   */
  platform: AppPlatform | null;
  /** True when the shell is mounted under the `/` Dashboard route. Resolved
   * once by the shared layout (`useLocation()`) — see § 3a matrix above. */
  isHome: boolean;
  /** The Store connection page IS the fix: the notice never shows there. */
  onStoreConnectionPage: boolean;
}

export default function ConnectStoreCard({
  platform,
  isHome,
  onStoreConnectionPage,
}: ConnectStoreCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedAppId } = useSelectedAppContext();

  // BOTH store sessions are per-app (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING
  // Track A for ASC, Track B for Play), keyed by the SELECTED APP id — never
  // the workspace id (the backend `_load_app_or_404`s the path id against
  // `App.app_id`; a workspace id there 404s). The shell reads them through the
  // ONE integrations-status summary (ENG-APP-UX-TUNEUP floor row 11) and
  // branches on `platform` to read only the matching session.
  const summary = useIntegrationsStatusSummary(selectedAppId);

  if (!platform) return null;

  const sessionQuery = {
    isLoading: summary.isLoading,
    data:
      platform === "ios"
        ? summary.data?.asc_session
        : summary.data?.play_console_session,
  };

  // Avoid a connect-then-collapse flash: while the platform-matched status
  // is still loading we render nothing. Once it resolves, branch on
  // connected/not-connected per the § 3a matrix above.
  if (sessionQuery.isLoading) return null;

  // The same product name the Needs-you row and the bell print, from the
  // same function -- one name for one blocker (C9 W5, pass-3 CR-012).
  const consoleLabel = consoleName(t, platform);

  if (sessionQuery.data?.connected) {
    // Matrix row "Connect your store console (connected)": not shown on any
    // page. On the Dashboard the Health panel's browser-session row carries
    // the health status now (§ 12 § 3.4 item 4, Rule 6 — one row per
    // credential, with its state word and the age of its last read), so the
    // one-line "connected" card retired into it.
    return null;
  }

  // Not connected. On the Dashboard the notice is Needs-you row 1 (below);
  // everywhere else the notice is ONE line — min 48 px, the console name as the bold lead,
  // one clause, one button, NOT dismissible: it clears itself on connect
  // (§ 12 § 2.3 "Upper notice (strip)" + § 3.1; Track C question 4,
  // baseline-audit B3: the stacked cards cost ~150 px above every h1). The
  // Store connection page IS the fix, so it never shows the notice.
  if (!isHome) {
    if (onStoreConnectionPage) return null;
    return (
      <section
        aria-labelledby="connect-store-strip-heading"
        className="border-danger-soft bg-danger-bg flex min-h-12 items-center gap-3 rounded-lg border px-4 py-2"
      >
        <PlugZap className="text-danger h-4 w-4 shrink-0" aria-hidden="true" />
        <p
          id="connect-store-strip-heading"
          className="text-ink min-w-0 flex-1 text-sm"
        >
          <strong className="font-semibold">
            {t("layout.upperNotice.storeConsole.lead", { consoleLabel })}
          </strong>{" "}
          <span className="text-ink-soft">
            {t("layout.upperNotice.storeConsole.clause")}
          </span>
        </p>
        <Button
          size="compact"
          onClick={() => navigate("/app/store-connections")}
        >
          {t("dashboard.needsYou.storeSession.connect", {
            console: consoleLabel,
          })}
        </Button>
      </section>
    );
  }

  // Matrix row "Connect your store console (not connected)" on the
  // Dashboard: not here. The Dashboard's Needs-you list carries it as row 1
  // (§ 12 § 3.1 "Not on the Dashboard (it is Needs you row 1)"; § 5 item 1),
  // from the same session read (`GET /api/v1/needs-you/{app_id}` composes the
  // handler this summary reads), so the two can never disagree.
  return null;
}
